import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const imgUrl = (rel) => {
  if (!rel) return "";
  try { return new URL(rel, API_BASE).toString(); } catch { return rel; }
};

/**
 * Minimal iframe-embeddable walkthrough viewer.
 * - Route 1: /embed/walkthrough/:slug   (specific featured demo)
 * - Route 2: /embed/demo-of-the-day     (rotates daily)
 * Designed for ~480x540 iframe; transparent background; minimal chrome.
 * Cross-origin safe (no auth, no cookies needed).
 */
export default function WalkthroughEmbed({ daily = false }) {
  const { slug: slugParam } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    const url = daily ? "/public/walkthroughs/demo-of-the-day" : `/public/walkthroughs/${slugParam}`;
    api.get(url)
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || "Demo not found"); });
    return () => { alive = false; };
  }, [daily, slugParam]);

  const totalSteps = data?.steps?.length || 0;
  const step = data?.steps?.[idx];

  const prev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);
  const next = useCallback(() => setIdx((i) => Math.min(totalSteps - 1, i + 1)), [totalSteps]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next]);

  // Estima homepage URL (for the CTA / footer attribution)
  const home = (typeof window !== "undefined" ? window.location.origin : "");

  if (err) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 text-center" data-testid="walkthrough-embed-error">
        <div>
          <p className="text-muted-foreground text-sm mb-3">{err}</p>
          <a href={home} target="_blank" rel="noopener noreferrer" className="btn-primary text-xs px-3 py-2">Open Estima →</a>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground text-sm" data-testid="walkthrough-embed-loading">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="walkthrough-embed">
      {/* Compact header */}
      <header className="px-4 py-2.5 border-b hairline flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--secondary))]">{data.category}{daily && " · Demo of the day"}</div>
          <div className="font-serif text-sm truncate" data-testid="walkthrough-embed-title">{data.title}</div>
        </div>
        <a
          href={`${home}/login`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] px-2 py-1 border border-[hsl(var(--secondary))] text-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))] hover:text-primary-foreground transition whitespace-nowrap"
          data-testid="walkthrough-embed-cta"
        >
          Try free →
        </a>
      </header>

      {/* Slide */}
      <div className="flex-1 overflow-y-auto" data-testid={`walkthrough-embed-step-${idx + 1}`}>
        {step?.image_url && (
          <div className="bg-black/40">
            <img
              src={imgUrl(step.image_url)}
              alt={step.caption}
              className="w-full h-auto block max-h-[300px] object-contain mx-auto"
              data-testid="walkthrough-embed-image"
            />
          </div>
        )}
        <div className="px-4 py-3 border-t hairline">
          <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--secondary))] mb-0.5">Step {idx + 1} of {totalSteps}</div>
          <p className="text-xs leading-relaxed" data-testid="walkthrough-embed-caption">{step?.caption}</p>
        </div>
      </div>

      {/* Footer controls */}
      <footer className="px-3 py-2 border-t hairline flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {data.steps.map((s, i) => (
            <button
              key={s.id || `dot-${i}`}
              onClick={() => setIdx(i)}
              className={`h-0.5 transition-all ${i === idx ? "w-5 bg-[hsl(var(--secondary))]" : "w-3 bg-muted"}`}
              aria-label={`Step ${i + 1}`}
              data-testid={`walkthrough-embed-dot-${i + 1}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={prev}
            disabled={idx === 0}
            className="text-[10px] px-2 py-1 border hairline inline-flex items-center disabled:opacity-30"
            data-testid="walkthrough-embed-prev"
          >
            <CaretLeft size={10} />
          </button>
          <button
            onClick={next}
            disabled={idx === totalSteps - 1}
            className="text-[10px] px-2 py-1 border hairline inline-flex items-center disabled:opacity-30"
            data-testid="walkthrough-embed-next"
          >
            <CaretRight size={10} />
          </button>
        </div>
        <a
          href={home}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[9px] text-muted-foreground hover:text-foreground transition"
          data-testid="walkthrough-embed-attribution"
        >
          Powered by Estima
        </a>
      </footer>
    </div>
  );
}
